import React, { useMemo } from "react";
import { Search, HelpCircle, BarChart2, Menu, Folder, ChevronDown, Check } from "lucide-react";
import { cn } from "../../utils/cn";
import { UserProfileMenu } from "../ui/UserProfileMenu";
import { ActionMenu } from "../ui/ActionMenu";
import { Project, User } from "../../types";

interface HeaderProps {
    isMobile: boolean;
    activeView: string;
    setActiveView: (view: string) => void;

    projects: Project[];
    selectedProjectIds: string[];
    setSelectedProjectIds: (ids: string[]) => void;
    isProjectDropdownOpen: boolean;
    setIsProjectDropdownOpen: (isOpen: boolean) => void;

    filteredEpicsCount: number;
    searchQuery: string;
    setSearchQuery: (query: string) => void;

    onAddEpic?: () => void;
    onFilter: () => void;
    activeFilterCount: number;
    onHelp: () => void;
    users: User[];
}

const ProjectPriorityDot = ({ priority }: { priority?: string }) => {
    switch (priority?.toLowerCase()) {
        case 'critical': return <div className="w-2 h-2 rounded-full bg-red-500" title="Critical" />;
        case 'high': return <div className="w-2 h-2 rounded-full bg-orange-500" title="High" />;
        case 'medium': return <div className="w-2 h-2 rounded-full bg-blue-500" title="Medium" />;
        case 'low': return <div className="w-2 h-2 rounded-full bg-slate-400" title="Low" />;
        default: return null;
    }
};

const priorityOrder: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };

export function Header({
    isMobile,
    activeView,
    setActiveView,
    projects,
    selectedProjectIds,
    setSelectedProjectIds,
    isProjectDropdownOpen,
    setIsProjectDropdownOpen,
    filteredEpicsCount,
    searchQuery,
    setSearchQuery,
    onAddEpic,
    onFilter,
    activeFilterCount,
    onHelp,
    users
}: HeaderProps) {
    const selectedProjects = projects.filter(p => selectedProjectIds.includes(p.id));

    const getAssignedPMs = (project: Project) => {
        if (!project.pm) return [];
        const pmArray = Array.isArray(project.pm) ? project.pm : [project.pm];
        return pmArray
            .map(id => users.find(u => u.id === id))
            .filter(Boolean) as User[];
    };

    const getPMLabel = (project: Project) => {
        const pms = getAssignedPMs(project);
        if (pms.length === 0) return "Unassigned";
        return pms.map(u => u.displayName || u.email).join(", ");
    };

    // Sort projects for dropdown
    const sortedProjects = useMemo(() => {
        return [...projects].sort((a, b) => {
            const pA = priorityOrder[a.priority?.toLowerCase() || 'medium'] || 0;
            const pB = priorityOrder[b.priority?.toLowerCase() || 'medium'] || 0;
            if (pA !== pB) return pB - pA;
            return a.name.localeCompare(b.name);
        });
    }, [projects]);

    return (
        <header className={cn(
            "h-14 relative flex items-center justify-between px-4 border-b border-siemens-deep-blue/10 dark:border-siemens-divider bg-white/80 dark:bg-siemens-elevation-base-1/80 backdrop-blur-md sticky top-0 z-30",
            isMobile ? "pl-4" : "pl-4"
        )}>
            {/* Left: Project Selector */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="relative">
                    <button
                        onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
                        className="flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-white/5 px-2 py-1.5 rounded-lg transition-colors max-w-[200px] sm:max-w-xs"
                    >
                        <Folder className="w-5 h-5 text-siemens-petrol" />
                        <div className="text-left flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <div className="text-sm font-bold text-siemens-deep-blue dark:text-white truncate">
                                    {selectedProjects.length === 0
                                        ? "All Projects"
                                        : selectedProjects.length === 1
                                            ? selectedProjects[0].name
                                            : `${selectedProjects.length} Projects`}
                                </div>
                                {selectedProjects.length === 1 && <ProjectPriorityDot priority={selectedProjects[0].priority} />}
                            </div>
                            <div className="text-[10px] text-siemens-text-soft truncate leading-tight">
                                {filteredEpicsCount} EPICS
                            </div>
                        </div>
                        <ChevronDown className="w-4 h-4 text-siemens-text-soft opacity-50" />
                    </button>

                    {/* Dropdown */}
                    {isProjectDropdownOpen && (
                        <>
                            <div
                                className="fixed inset-0 z-40"
                                onClick={() => setIsProjectDropdownOpen(false)}
                            />
                            <div className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-siemens-elevation-base-1 border border-siemens-deep-blue/8 dark:border-siemens-divider rounded-xl shadow-xl z-50 py-1 max-h-[80vh] overflow-y-auto">
                                <div className="px-3 py-2 text-xs font-bold text-siemens-text-soft uppercase tracking-wider">
                                    Select Project
                                </div>
                                <button
                                    onClick={() => { setSelectedProjectIds([]); setIsProjectDropdownOpen(false); }}
                                    className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors flex items-center justify-between"
                                >
                                    <span className={cn("text-sm", selectedProjectIds.length === 0 ? "font-bold text-siemens-petrol" : "text-siemens-deep-blue dark:text-white")}>
                                        All Projects
                                    </span>
                                    {selectedProjectIds.length === 0 && <Check className="w-4 h-4 text-siemens-petrol" />}
                                </button>
                                {sortedProjects.map(p => {
                                    const isSelected = selectedProjectIds.includes(p.id);
                                    return (
                                        <button
                                            key={p.id}
                                            onClick={() => {
                                                if (isSelected) {
                                                    setSelectedProjectIds(selectedProjectIds.filter(id => id !== p.id));
                                                } else {
                                                    setSelectedProjectIds([...selectedProjectIds, p.id]);
                                                }
                                            }}
                                            className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors flex items-center justify-between group"
                                        >
                                            <div className="min-w-0 flex-1 pr-2">
                                                <div className="flex items-center gap-2">
                                                    <div className={cn("text-sm truncate", isSelected ? "font-bold text-siemens-petrol" : "text-siemens-deep-blue dark:text-white")}>
                                                        {p.name}
                                                    </div>
                                                    <ProjectPriorityDot priority={p.priority} />
                                                </div>
                                                <div className="text-xs text-siemens-text-soft mt-0.5 flex justify-between">
                                                    <span>{getPMLabel(p)}</span>
                                                </div>
                                            </div>
                                            {isSelected && <Check className="w-4 h-4 text-siemens-petrol" />}
                                        </button>
                                    );
                                })}
                                <div className="border-t border-siemens-deep-blue/5 dark:border-siemens-divider mt-1 pt-1">
                                    <button
                                        onClick={() => setActiveView('projects')}
                                        className="w-full text-left px-4 py-3 text-xs font-bold text-siemens-petrol hover:text-siemens-petrol-dark transition-colors"
                                    >
                                        Manage Projects...
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Center: Search */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md lg:max-w-xl px-4 hidden md:block">
                <div className="relative w-full group">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-siemens-text-soft group-focus-within:text-siemens-petrol transition-colors" />
                    <input
                        className="w-full pl-9 pr-3 h-10 text-sm rounded-xl border-none bg-siemens-deep-blue-8/30 dark:bg-white/5 focus:bg-white dark:focus:bg-[#1a1a24] focus:ring-2 focus:ring-siemens-petrol/20 transition-all text-siemens-deep-blue dark:text-white placeholder:text-siemens-text-soft"
                        placeholder="Search epics, owners, or IDs..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-1 sm:gap-2">
                {!isMobile && (
                    <>
                        <ActionMenu onAdd={onAddEpic} onFilter={onFilter} activeFilterCount={activeFilterCount} />
                        <div className="w-px h-6 bg-siemens-deep-blue/10 dark:bg-siemens-divider mx-1" />
                        <button onClick={onHelp} className="p-2 rounded-lg text-siemens-text-soft hover:bg-slate-100 dark:hover:bg-white/5 transition-colors">
                            <HelpCircle className="w-5 h-5" />
                        </button>
                        <a
                            href="https://icy-smoke-028c26303.3.azurestaticapps.net/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg text-siemens-text-soft hover:bg-slate-100 dark:hover:bg-white/5 hover:text-siemens-petrol transition-colors"
                            title="ZPS Dashboard"
                        >
                            <BarChart2 className="w-5 h-5" />
                        </a>
                        <div className="w-px h-6 bg-siemens-deep-blue/10 dark:bg-siemens-divider mx-1" />
                    </>
                )}
                <UserProfileMenu />
            </div>
        </header>
    );
}
